import React, { useState, useEffect, useCallback, useRef } from 'react';
import DashboardLayout from '../../components/common/DashboardLayout';
import RichTextEditor from '../../components/common/RichTextEditor';
import {
  listAudiences, createMemberGroup, deleteMemberGroup,
  getMemberGroupMembers, addMemberGroupMembers, removeMemberGroupMember,
  previewBulkEmail, getAdminMembers,
  listBulkEmails, createBulkEmail, updateBulkEmail, deleteBulkEmail,
  scheduleBulkEmail, unscheduleBulkEmail, sendBulkEmailNow,
} from '../../services/admin.service';

const HEADER_STYLES = [
  { key: 'jenn_photo', label: "Hey, it's Jenn", description: 'Your photo banner' },
  { key: 'compass',    label: 'Compass',         description: 'Dark banner, gold compass icon — matches the weekly summary' },
];

const BLOCK_TYPES = [
  { type: 'heading', label: 'Heading' },
  { type: 'text',     label: 'Text' },
  { type: 'image',    label: 'Image' },
  { type: 'button',   label: 'Button' },
  { type: 'divider',  label: 'Divider' },
  { type: 'spacer',   label: 'Spacer' },
];

const blankBlock = (type) => {
  switch (type) {
    case 'heading': return { type, key: crypto.randomUUID(), text: '' };
    case 'text':     return { type, key: crypto.randomUUID(), content: '' };
    case 'image':    return { type, key: crypto.randomUUID(), url: '', alt: '', linkUrl: '' };
    case 'button':   return { type, key: crypto.randomUUID(), text: 'Access my Dashboard', url: '' };
    case 'divider':  return { type, key: crypto.randomUUID() };
    case 'spacer':   return { type, key: crypto.randomUUID(), height: 20 };
    default:         return { type, key: crypto.randomUUID() };
  }
};

// ─── Block editor row ───────────────────────────────────────────────────────

function BlockEditor({ block, onChange, onRemove, onMoveUp, onMoveDown, isFirst, isLast }) {
  const set = (fields) => onChange({ ...block, ...fields });

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">
          {BLOCK_TYPES.find(b => b.type === block.type)?.label || block.type}
        </span>
        <div className="flex items-center gap-1">
          <button onClick={onMoveUp} disabled={isFirst} className="text-xs text-gray-400 hover:text-reset-brown disabled:opacity-30 px-1.5">↑</button>
          <button onClick={onMoveDown} disabled={isLast} className="text-xs text-gray-400 hover:text-reset-brown disabled:opacity-30 px-1.5">↓</button>
          <button onClick={onRemove} className="text-xs text-red-400 hover:text-red-600 px-1.5">Remove</button>
        </div>
      </div>

      {block.type === 'heading' && (
        <input
          type="text" value={block.text} onChange={e => set({ text: e.target.value })}
          placeholder="Heading text"
          className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2"
        />
      )}

      {block.type === 'text' && (
        <RichTextEditor
          value={block.content}
          onChange={(content) => set({ content })}
          minHeightClass="min-h-[100px]"
          placeholder="Write your text — select text to bold or italicize it."
        />
      )}

      {block.type === 'image' && (
        <div className="space-y-2">
          <input
            type="text" value={block.url} onChange={e => set({ url: e.target.value })}
            placeholder="Image URL"
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2"
          />
          <input
            type="text" value={block.alt} onChange={e => set({ alt: e.target.value })}
            placeholder="Alt text (describe the image)"
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2"
          />
          <input
            type="text" value={block.linkUrl} onChange={e => set({ linkUrl: e.target.value })}
            placeholder="Optional — link the image goes to when clicked"
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2"
          />
        </div>
      )}

      {block.type === 'button' && (
        <div className="space-y-2">
          <input
            type="text" value={block.text} onChange={e => set({ text: e.target.value })}
            placeholder="Button text"
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2"
          />
          <input
            type="text" value={block.url} onChange={e => set({ url: e.target.value })}
            placeholder="Where the button links to"
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2"
          />
        </div>
      )}

      {block.type === 'divider' && (
        <p className="text-xs text-gray-400">A horizontal line — no settings needed.</p>
      )}

      {block.type === 'spacer' && (
        <div className="flex items-center gap-2">
          <input
            type="number" value={block.height} onChange={e => set({ height: e.target.value })}
            className="w-24 text-sm border border-gray-200 rounded-lg px-3 py-2"
          />
          <span className="text-xs text-gray-400">pixels of blank space</span>
        </div>
      )}
    </div>
  );
}

// ─── Compose & Send tab ─────────────────────────────────────────────────────

const audienceToValue = (audience) => {
  if (!audience) return '';
  return audience.type === 'smart' ? `smart:${audience.key}` : `custom:${audience.groupId}`;
};

function ComposeTab({ audiences, reloadAudiences, initialEmail, onSaved, goToSaved }) {
  const [emailId, setEmailId] = useState(initialEmail?.id || null);
  const [subject, setSubject] = useState(initialEmail?.subject || '');
  const [headerStyle, setHeaderStyle] = useState(initialEmail?.header_style || 'jenn_photo');
  const [blocks, setBlocks] = useState(initialEmail?.blocks || []);
  const [audienceValue, setAudienceValue] = useState(audienceToValue(initialEmail?.audience));
  const [previewHtml, setPreviewHtml] = useState('');
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleAt, setScheduleAt] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const previewTimer = useRef(null);
  const previewFrameRef = useRef(null);
  const [previewHeight, setPreviewHeight] = useState(600);

  // The preview grows to fit the actual email instead of a fixed-height
  // inner scrollbox — Jenn, 2026-09-01: "I can't even show you the final
  // one because the scroll box is way too short." A real email can run to
  // several thousand pixels; the page itself should scroll, not a cramped
  // box inside it.
  const handlePreviewLoad = () => {
    const doc = previewFrameRef.current?.contentDocument;
    if (doc?.body) setPreviewHeight(Math.max(600, doc.body.scrollHeight + 20));
  };

  useEffect(() => {
    clearTimeout(previewTimer.current);
    if (blocks.length === 0) { setPreviewHtml(''); return; }
    previewTimer.current = setTimeout(async () => {
      try {
        const { html } = await previewBulkEmail(blocks, headerStyle);
        setPreviewHtml(html);
      } catch { /* non-critical */ }
    }, 400);
    return () => clearTimeout(previewTimer.current);
  }, [blocks, headerStyle]);

  const addBlock = (type) => setBlocks(bs => [...bs, blankBlock(type)]);
  const updateBlock = (idx, next) => setBlocks(bs => bs.map((b, i) => i === idx ? next : b));
  const removeBlock = (idx) => setBlocks(bs => bs.filter((_, i) => i !== idx));
  const moveBlock = (idx, dir) => setBlocks(bs => {
    const next = [...bs];
    const [item] = next.splice(idx, 1);
    next.splice(idx + dir, 0, item);
    return next;
  });

  const selectedAudience = () => {
    if (!audienceValue) return null;
    const [type, key] = audienceValue.split(':');
    return type === 'smart' ? { type: 'smart', key } : { type: 'custom', groupId: key };
  };

  const recipientCount = () => {
    if (!audienceValue) return null;
    const [type, key] = audienceValue.split(':');
    if (type === 'smart') return audiences.smart.find(g => g.key === key)?.count ?? 0;
    return audiences.custom.find(g => g.id === key)?.member_count ?? 0;
  };

  const currentPayload = () => ({
    subject: subject.trim(), headerStyle, blocks, audience: selectedAudience(),
  });

  /** Creates the saved row on first save, updates it on every save after. Returns the (possibly new) id. */
  const ensureSaved = async () => {
    if (emailId) {
      await updateBulkEmail(emailId, currentPayload());
      return emailId;
    }
    const created = await createBulkEmail(currentPayload());
    setEmailId(created.id);
    onSaved?.();
    return created.id;
  };

  const handleSaveDraft = async () => {
    setError(null);
    if (!subject.trim()) { setError('Give this email a subject.'); return; }
    setSaving(true);
    try {
      await ensureSaved();
      setResult({ saved: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save this draft.');
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async () => {
    setError(null);
    const audience = selectedAudience();
    if (!audience) { setError('Choose who this goes to.'); return; }
    if (!subject.trim()) { setError('Give this email a subject.'); return; }
    if (blocks.length === 0) { setError('Add at least one block.'); return; }

    const count = recipientCount();
    if (!window.confirm(`Send "${subject}" to ${count} member${count === 1 ? '' : 's'}? This can't be undone.`)) return;

    setSending(true);
    try {
      const id = await ensureSaved();
      const res = await sendBulkEmailNow(id);
      setResult(res);
      reloadAudiences();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not send this email.');
    } finally {
      setSending(false);
    }
  };

  const handleSchedule = async () => {
    setError(null);
    const audience = selectedAudience();
    if (!audience) { setError('Choose who this goes to.'); return; }
    if (!subject.trim()) { setError('Give this email a subject.'); return; }
    if (blocks.length === 0) { setError('Add at least one block.'); return; }
    if (!scheduleAt) { setError('Pick a date and time.'); return; }

    setSaving(true);
    try {
      const id = await ensureSaved();
      await scheduleBulkEmail(id, new Date(scheduleAt).toISOString());
      setResult({ scheduled: true, when: scheduleAt });
      setShowSchedule(false);
      goToSaved?.();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not schedule this email.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <div className="space-y-4">
        {emailId && (
          <p className="text-xs text-gray-400">Editing a saved draft. <button onClick={() => window.location.reload()} className="underline hover:text-reset-terracotta">Start a new email instead</button></p>
        )}
        <div className="bg-white rounded-2xl shadow-sm p-5 space-y-3">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Subject</label>
            <input
              type="text" value={subject} onChange={e => setSubject(e.target.value)}
              placeholder="e.g. How to access your new membership"
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Header</label>
            <div className="flex flex-wrap gap-2">
              {HEADER_STYLES.map(hs => (
                <button
                  key={hs.key}
                  onClick={() => setHeaderStyle(hs.key)}
                  className={`text-left px-3 py-2 rounded-lg border text-xs transition-colors ${
                    headerStyle === hs.key
                      ? 'border-reset-terracotta bg-reset-terracotta/5'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="font-medium text-reset-brown block">{hs.label}</span>
                  <span className="text-gray-400">{hs.description}</span>
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Send to</label>
            <select
              value={audienceValue} onChange={e => setAudienceValue(e.target.value)}
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2"
            >
              <option value="">Choose an audience…</option>
              <optgroup label="Smart groups">
                {audiences.smart.map(g => (
                  <option key={g.key} value={`smart:${g.key}`}>{g.label} ({g.count})</option>
                ))}
              </optgroup>
              {audiences.custom.length > 0 && (
                <optgroup label="Custom groups">
                  {audiences.custom.map(g => (
                    <option key={g.id} value={`custom:${g.id}`}>{g.name} ({g.member_count})</option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>
        </div>

        <div className="space-y-3">
          {blocks.map((block, idx) => (
            <BlockEditor
              key={block.key}
              block={block}
              onChange={(next) => updateBlock(idx, next)}
              onRemove={() => removeBlock(idx)}
              onMoveUp={() => moveBlock(idx, -1)}
              onMoveDown={() => moveBlock(idx, 1)}
              isFirst={idx === 0}
              isLast={idx === blocks.length - 1}
            />
          ))}
        </div>

        <div className="bg-white rounded-2xl shadow-sm p-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Add a block</p>
          <div className="flex flex-wrap gap-2">
            {BLOCK_TYPES.map(bt => (
              <button
                key={bt.type} onClick={() => addBlock(bt.type)}
                className="text-xs font-medium px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:border-reset-terracotta hover:text-reset-terracotta transition-colors"
              >
                + {bt.label}
              </button>
            ))}
          </div>
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}
        {result?.saved && <p className="text-sm text-reset-sage">Draft saved. You can come back to it from "Saved & Scheduled."</p>}
        {result?.scheduled && <p className="text-sm text-reset-sage">Scheduled for {new Date(result.when).toLocaleString(undefined, { hour12: true })}.</p>}
        {result?.sent != null && (
          <p className="text-sm text-reset-sage">Sent to {result.sent} of {result.total} ({result.failed} failed).</p>
        )}

        {showSchedule && (
          <div className="bg-white rounded-2xl shadow-sm p-4 space-y-2">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide">Send at</label>
            <input
              type="datetime-local" value={scheduleAt} onChange={e => setScheduleAt(e.target.value)}
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2"
            />
            <div className="flex gap-2">
              <button onClick={handleSchedule} disabled={saving} className="text-sm font-medium bg-reset-brown text-white px-4 py-2 rounded-lg disabled:opacity-50">
                {saving ? 'Scheduling…' : 'Confirm schedule'}
              </button>
              <button onClick={() => setShowSchedule(false)} className="text-sm text-gray-500 px-4 py-2">Cancel</button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-2">
          <button
            onClick={handleSaveDraft}
            disabled={saving}
            className="bg-white border border-gray-200 text-gray-600 font-medium py-3 rounded-xl disabled:opacity-50 text-sm"
          >
            {saving ? 'Saving…' : 'Save draft'}
          </button>
          <button
            onClick={() => setShowSchedule(v => !v)}
            className="bg-white border border-gray-200 text-gray-600 font-medium py-3 rounded-xl text-sm"
          >
            Schedule…
          </button>
          <button
            onClick={handleSend}
            disabled={sending}
            className="bg-reset-terracotta text-white font-medium py-3 rounded-xl disabled:opacity-50 text-sm"
          >
            {sending ? 'Sending…' : `Send${recipientCount() != null ? ` (${recipientCount()})` : ' now'}`}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm p-4">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Live preview</p>
        {previewHtml ? (
          <iframe
            ref={previewFrameRef}
            title="Bulk email preview"
            srcDoc={previewHtml}
            sandbox="allow-same-origin"
            onLoad={handlePreviewLoad}
            className="w-full rounded-lg border border-gray-100"
            style={{ height: `${previewHeight}px` }}
          />
        ) : (
          <p className="text-sm text-gray-400 italic">Add a block to see the preview.</p>
        )}
      </div>
    </div>
  );
}

// ─── Manage Groups tab ──────────────────────────────────────────────────────

function GroupsTab({ audiences, reloadAudiences }) {
  const [newGroupName, setNewGroupName] = useState('');
  const [expandedGroupId, setExpandedGroupId] = useState(null);
  const [groupMembers, setGroupMembers] = useState([]);
  const [allMembers, setAllMembers] = useState([]);
  const [search, setSearch] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => { getAdminMembers().then(setAllMembers).catch(() => {}); }, []);

  const loadGroupMembers = async (groupId) => {
    setExpandedGroupId(groupId);
    setSearch('');
    try {
      setGroupMembers(await getMemberGroupMembers(groupId));
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load this group.');
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) return;
    try {
      await createMemberGroup(newGroupName.trim());
      setNewGroupName('');
      reloadAudiences();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not create this group.');
    }
  };

  const handleDeleteGroup = async (group) => {
    if (!window.confirm(`Delete the group "${group.name}"? This doesn't affect the members themselves.`)) return;
    await deleteMemberGroup(group.id);
    if (expandedGroupId === group.id) setExpandedGroupId(null);
    reloadAudiences();
  };

  const handleAddMember = async (memberId) => {
    await addMemberGroupMembers(expandedGroupId, [memberId]);
    loadGroupMembers(expandedGroupId);
    reloadAudiences();
  };

  const handleRemoveMember = async (memberId) => {
    await removeMemberGroupMember(expandedGroupId, memberId);
    loadGroupMembers(expandedGroupId);
    reloadAudiences();
  };

  const inGroup = new Set(groupMembers.map(m => m.id));
  const searchResults = search.trim().length > 0
    ? allMembers.filter(m =>
        !inGroup.has(m.id) &&
        ((m.name || '').toLowerCase().includes(search.toLowerCase()) || (m.email || '').toLowerCase().includes(search.toLowerCase()))
      ).slice(0, 8)
    : [];

  return (
    <div className="grid md:grid-cols-2 gap-5">
      <div className="bg-white rounded-2xl shadow-sm p-5">
        <p className="font-semibold text-sm text-reset-brown mb-3">Your custom groups</p>
        <div className="flex gap-2 mb-4">
          <input
            type="text" value={newGroupName} onChange={e => setNewGroupName(e.target.value)}
            placeholder="New group name"
            className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2"
          />
          <button onClick={handleCreateGroup} className="text-sm font-medium bg-reset-terracotta text-white px-4 py-2 rounded-lg">
            Create
          </button>
        </div>
        {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
        {audiences.custom.length === 0 ? (
          <p className="text-sm text-gray-400">No custom groups yet.</p>
        ) : (
          <div className="space-y-1">
            {audiences.custom.map(g => (
              <div key={g.id} className={`flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer ${expandedGroupId === g.id ? 'bg-reset-cream' : 'hover:bg-gray-50'}`} onClick={() => loadGroupMembers(g.id)}>
                <span className="text-sm text-reset-brown">{g.name} <span className="text-gray-400">({g.member_count})</span></span>
                <button onClick={(e) => { e.stopPropagation(); handleDeleteGroup(g); }} className="text-xs text-red-400 hover:text-red-600">Delete</button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl shadow-sm p-5">
        {!expandedGroupId ? (
          <p className="text-sm text-gray-400">Select a group to see and edit its members.</p>
        ) : (
          <>
            <p className="font-semibold text-sm text-reset-brown mb-3">
              {audiences.custom.find(g => g.id === expandedGroupId)?.name}
            </p>
            <div className="space-y-1 mb-4 max-h-48 overflow-y-auto">
              {groupMembers.length === 0 && <p className="text-sm text-gray-400">No members yet.</p>}
              {groupMembers.map(m => (
                <div key={m.id} className="flex items-center justify-between px-3 py-1.5 rounded-lg hover:bg-gray-50">
                  <span className="text-sm text-reset-brown">{m.name} <span className="text-gray-400">— {m.email}</span></span>
                  <button onClick={() => handleRemoveMember(m.id)} className="text-xs text-red-400 hover:text-red-600">Remove</button>
                </div>
              ))}
            </div>
            <input
              type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search members by name or email to add…"
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 mb-2"
            />
            {searchResults.length > 0 && (
              <div className="border border-gray-100 rounded-lg divide-y divide-gray-50 max-h-48 overflow-y-auto">
                {searchResults.map(m => (
                  <button key={m.id} onClick={() => handleAddMember(m.id)} className="w-full text-left px-3 py-2 text-sm hover:bg-reset-cream/60">
                    {m.name} <span className="text-gray-400">— {m.email}</span>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Saved & Scheduled tab ──────────────────────────────────────────────────

const STATUS_LABEL = { draft: 'Draft', scheduled: 'Scheduled', sent: 'Sent' };
const STATUS_COLOR = { draft: 'bg-gray-100 text-gray-600', scheduled: 'bg-amber-100 text-amber-700', sent: 'bg-green-100 text-green-700' };

function SavedTab({ onEdit, refreshKey }) {
  const [emails, setEmails] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => { listBulkEmails().then(setEmails).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const handleDelete = async (email) => {
    if (!window.confirm(`Delete "${email.subject || '(no subject)'}"? This can't be undone.`)) return;
    await deleteBulkEmail(email.id);
    load();
  };

  const handleUnschedule = async (email) => {
    await unscheduleBulkEmail(email.id);
    load();
  };

  const handleSendNow = async (email) => {
    if (!window.confirm(`Send "${email.subject}" right now?`)) return;
    try {
      const res = await sendBulkEmailNow(email.id);
      alert(`Sent to ${res.sent} of ${res.total} (${res.failed} failed).`);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not send this email.');
    }
  };

  if (!emails) return <p className="text-sm text-gray-400">Loading…</p>;

  return (
    <div className="bg-white rounded-2xl shadow-sm p-5">
      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
      {emails.length === 0 ? (
        <p className="text-sm text-gray-400">Nothing saved yet — drafts and scheduled emails from the Compose tab show up here.</p>
      ) : (
        <div className="space-y-2">
          {emails.map(email => (
            <div key={email.id} className="flex items-center justify-between px-4 py-3 rounded-xl border border-gray-100">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${STATUS_COLOR[email.status]}`}>{STATUS_LABEL[email.status]}</span>
                  <span className="text-sm font-medium text-reset-brown truncate">{email.subject || '(no subject)'}</span>
                </div>
                <p className="text-xs text-gray-400">
                  {email.status === 'scheduled' && `Sends ${new Date(email.scheduled_for).toLocaleString(undefined, { hour12: true })}`}
                  {email.status === 'sent' && `Sent ${new Date(email.sent_at).toLocaleString(undefined, { hour12: true })} — ${email.sent_count ?? 0} recipient(s)`}
                  {email.status === 'draft' && `Last edited ${new Date(email.updated_at).toLocaleString(undefined, { hour12: true })}`}
                </p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {email.status !== 'sent' && (
                  <button onClick={() => onEdit(email)} className="text-xs font-medium text-gray-600 hover:text-reset-terracotta px-2 py-1">Edit</button>
                )}
                {email.status === 'draft' && (
                  <button onClick={() => handleSendNow(email)} className="text-xs font-medium text-reset-terracotta px-2 py-1">Send now</button>
                )}
                {email.status === 'scheduled' && (
                  <button onClick={() => handleUnschedule(email)} className="text-xs font-medium text-gray-600 hover:text-reset-terracotta px-2 py-1">Unschedule</button>
                )}
                {email.status !== 'sent' && (
                  <button onClick={() => handleDelete(email)} className="text-xs text-red-400 hover:text-red-600 px-2 py-1">Delete</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────

export default function AdminBulkEmail() {
  const [tab, setTab] = useState('compose');
  const [audiences, setAudiences] = useState(null);
  const [editingEmail, setEditingEmail] = useState(null);
  const [composeKey, setComposeKey] = useState(0);
  const [savedRefreshKey, setSavedRefreshKey] = useState(0);

  const reloadAudiences = useCallback(() => {
    listAudiences().then(setAudiences).catch(() => {});
  }, []);

  useEffect(() => { reloadAudiences(); }, [reloadAudiences]);

  const startEditing = (email) => {
    setEditingEmail(email);
    setComposeKey(k => k + 1);
    setTab('compose');
  };

  const tabs = [
    { key: 'compose', label: 'Compose & Send' },
    { key: 'saved', label: 'Saved & Scheduled' },
    { key: 'groups', label: 'Manage Groups' },
  ];

  return (
    <DashboardLayout>
      <h1 className="font-serif text-2xl font-bold text-reset-brown mb-1">Groups & Bulk Email</h1>
      <p className="text-sm text-gray-500 mb-5">Build an email out of blocks and send it to any group of members.</p>

      <div className="flex gap-2 mb-5">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} className={`text-sm font-medium px-4 py-2 rounded-lg ${tab === t.key ? 'bg-reset-brown text-white' : 'bg-white text-gray-600 border border-gray-200'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {!audiences ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : tab === 'compose' ? (
        <ComposeTab
          key={composeKey}
          audiences={audiences}
          reloadAudiences={reloadAudiences}
          initialEmail={editingEmail}
          onSaved={() => setSavedRefreshKey(k => k + 1)}
          goToSaved={() => { setSavedRefreshKey(k => k + 1); setTab('saved'); }}
        />
      ) : tab === 'saved' ? (
        <SavedTab onEdit={startEditing} refreshKey={savedRefreshKey} />
      ) : (
        <GroupsTab audiences={audiences} reloadAudiences={reloadAudiences} />
      )}
    </DashboardLayout>
  );
}
